import React, { useCallback, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FacebookIcon } from "../../social/Facebook/FacebookIcon";
import { InstagramIcon } from "../../social/Instagram/InstagramIcon";
import { LinkedInIcon } from "../../social/LinkedIn/LinkedInIcon";
import { LinkedInBusinessIcon } from "../../social/LinkedIn/LinkedInBusinessIcon";
import { TiktokIcon } from "../../social/Tiktok/TiktokIcon";
import { SocialProviderId, isInstagramSocialConnected } from "../../../../cores/socialPost/config";
import FacebookAuthButton from "../../social/Facebook/FacebookAuthButton";
import InstagramConnectionSelector from "../../social/InstagramLogin/InstagramConnectionSelector";
import LinkedInAuthButton from "../../social/LinkedIn/LinkedInBindCard";
import LinkedInBusinessAuthButton from "../../social/LinkedIn/LinkedInBusinessBindCard";
import TiktokAuthButton from "../../social/Tiktok/TiktokBindCard";
import YoutubeAuthButton from "../../social/Youtube/YoutubeBindCard";
import { YoutubeIcon } from "../../social/Youtube/YoutubeIcon";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../../cores/me";
import {
  isTiktokPostingEnabled,
  isTiktokSocialProviderId,
  isYoutubePostingEnabled,
  isYoutubeSocialProviderId,
} from "../../../../cores/config/featureFlags";
import { isYoutubeConnected, useYoutubeCore } from "../../../../cores/youtube";
import "./ConnectSocialModal.scss";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onConnect: (providerId: string) => void;
}

export const ConnectSocialModal = ({ isOpen, toggle, onConnect }: Props) => {
  const intl = useIntl();
  const { socialSettings } = useMeCore();
  const { settings: youtubeSettings } = useYoutubeCore();
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [selectedProviderWasConnected, setSelectedProviderWasConnected] =
    useState(false);

  const allProviders = [
    {
      id: SocialProviderId.Facebook,
      name: "Facebook",
      icon: "facebook",
      color: "#1877F2",
      description: intl.formatMessage({ id: "app.connectSocialModal.facebookDescription" }),
      iconComponent: <FacebookIcon size="2x" color="#1877F2" />,
      authComponent: <FacebookAuthButton />
    },
    {
      id: SocialProviderId.Instagram,
      name: "Instagram",
      icon: "instagram", 
      color: "#E4405F",
      description: intl.formatMessage({ id: "app.connectSocialModal.instagramDescription" }),
      iconComponent: <InstagramIcon size="2x" color="#E4405F" />,
      authComponent: <InstagramConnectionSelector />
    },
    {
      id: SocialProviderId.LinkedIn,
      name: "LinkedIn",
      icon: "linkedIn",
      color: "#0077B5",
      description: intl.formatMessage({ id: "app.connectSocialModal.linkedInDescription" }),
      iconComponent: <LinkedInIcon size="2x" color="#0077B5" />,
      authComponent: <LinkedInAuthButton />
    },
    {
      id: SocialProviderId.LinkedInBusiness,
      name: "LinkedIn Business",
      icon: "linkedInBusiness",
      color: "#0077B5",
      description: intl.formatMessage({ id: "app.connectSocialModal.linkedInBusinessDescription" }),
      iconComponent: <LinkedInBusinessIcon size="2x" color="#0077B5" />,
      authComponent: <LinkedInBusinessAuthButton />
    },
    {
      id: SocialProviderId.TikTok,
      name: "TikTok",
      icon: "tiktok",
      color: "#000000",
      description: intl.formatMessage({ id: "app.connectSocialModal.tiktokDescription" }),
      iconComponent: <TiktokIcon size="2x" color="var(--tiktok-icon-color)" />,
      authComponent: <TiktokAuthButton />
    },
    {
      id: SocialProviderId.YouTube,
      name: "YouTube",
      icon: "youtube",
      color: "#FF0000",
      description: intl.formatMessage({ id: "app.connectSocialModal.youtubeDescription" }),
      iconComponent: <YoutubeIcon size="2x" color="#FF0000" />,
      authComponent: <YoutubeAuthButton />
    }
  ];

  const availableProviders = allProviders.filter(
    (p) =>
      (isTiktokPostingEnabled() || !isTiktokSocialProviderId(p.id)) &&
      (isYoutubePostingEnabled() || !isYoutubeSocialProviderId(p.id))
  );

  const isProviderConnected = useCallback(
    (providerId: string) => {
      const settingsKeyByProvider: Record<string, string> = {
        [SocialProviderId.Facebook]: "facebook",
        [SocialProviderId.Instagram]: "instagram",
        [SocialProviderId.LinkedIn]: "linkedIn",
        [SocialProviderId.LinkedInBusiness]: "linkedInBusiness",
        [SocialProviderId.TikTok]: "tiktok",
        [SocialProviderId.YouTube]: "youtube",
      };

      const settingsKey = settingsKeyByProvider[providerId];
      const providerSettings = settingsKey
        ? (socialSettings as any)?.[settingsKey]
        : null;

      if (providerId === SocialProviderId.YouTube) {
        return Boolean(
          providerSettings ||
            isYoutubeConnected(youtubeSettings)
        );
      }

      if (providerId === SocialProviderId.Instagram) {
        return isInstagramSocialConnected(socialSettings as Record<string, unknown>);
      }

      return Boolean(
        providerSettings &&
          (typeof providerSettings !== "object" ||
            Object.keys(providerSettings).length > 0)
      );
    },
    [socialSettings, youtubeSettings]
  );

  const resetAndClose = useCallback(() => {
    setSelectedProvider("");
    setSelectedProviderWasConnected(false);
    toggle();
  }, [toggle]);

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    setSelectedProviderWasConnected(isProviderConnected(providerId));
  };

  const handleConnect = () => {
    if (selectedProvider) {
      onConnect(selectedProvider);
      resetAndClose();
    }
  };

  const handleClose = () => {
    resetAndClose();
  };

  useEffect(() => {
    if (!isOpen || !selectedProvider || selectedProviderWasConnected) return;
    if (!isProviderConnected(selectedProvider)) return;

    onConnect(selectedProvider);
    resetAndClose();
  }, [
    isOpen,
    isProviderConnected,
    onConnect,
    resetAndClose,
    selectedProvider,
    selectedProviderWasConnected,
  ]);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" className="connect-social-modal">
      <ModalHeader toggle={handleClose}>
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          <span>{intl.formatMessage({ id: "app.connectSocialModal.title" })}</span>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="providers-grid">
          {availableProviders.map((provider) => (
            <div
              key={provider.id}
              className={`provider-card provider-card--${provider.icon} ${selectedProvider === provider.id ? 'selected' : ''}`}
              onClick={() => handleProviderSelect(provider.id)}
            >
              <div className="provider-icon">
                {provider.iconComponent}
              </div>
              <div className="provider-info">
                <h6 className="provider-name">{provider.name}</h6>
                <p className="provider-description">{provider.description}</p>
              </div>
              {selectedProvider === provider.id && (
                <div className="selected-indicator">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {selectedProvider && (
          <div className="mt-4">
            <div className="auth-section">
              <h6 className="auth-title">
                {intl.formatMessage(
                  { id: "app.connectSocialModal.connectProvider" },
                  { provider: availableProviders.find((p) => p.id === selectedProvider)?.name }
                )}
              </h6>
              <div className="auth-button-container">
                {availableProviders.find(p => p.id === selectedProvider)?.authComponent}
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          {intl.formatMessage({ id: "app.socialMediaPost.cancel" })}
        </Button>
        <Button 
          color="primary" 
          onClick={handleConnect}
          disabled={!selectedProvider}
        >
          {intl.formatMessage({ id: "app.home.syncUp" })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
